import {ApolloError} from '@apollo/client';
import {Alert, AlertTitle, Button} from '@mui/material';
import {Theme} from '@mui/material/styles';
import {FC} from 'react';
import {makeStyles} from 'tss-react/mui';

interface GraphQLErrorsProps {
    error: ApolloError;
    refetch?: () => any;
}

const parseErrorMessage = (message: string): string[] => {
    if (!message.startsWith('{') || !message.endsWith('}')) {
        return [message];
    }

    try {
        const jsonString = message.replace(/'/g, '"');
        const parsed = JSON.parse(jsonString);

        const messages: string[] = [];
        Object.values(parsed).forEach(errors => {
            if (Array.isArray(errors)) {
                messages.push(...errors);
            } else if (typeof errors === 'string') {
                messages.push(errors);
            }
        });

        return messages.length > 0 ? messages : [message];
    } catch {
        return [message];
    }
};

export const GraphQLErrors: FC<React.PropsWithChildren<GraphQLErrorsProps>> = ({error, refetch}) => {
    const {classes} = useStyles();

    const refetchButton = refetch && (
        <Button color='inherit' size='small' onClick={() => refetch()}>
            Reintentar
        </Button>
    );

    return (
        <div className={classes.root}>
            {error.graphQLErrors.map(({message}, index) => {
                const parsedMessages = parseErrorMessage(message);

                return (
                    <Alert key={`${message}-${index}`} severity='error' action={refetchButton}>
                        <AlertTitle>Error de validación</AlertTitle>
                        {parsedMessages.map((msg, msgIndex) => (
                            <div key={msgIndex} className={classes.errorMessage}>
                                {msg}
                            </div>
                        ))}
                    </Alert>
                );
            })}
            {error.networkError && (
                <Alert severity='error' action={refetchButton}>
                    <AlertTitle>Error de conexión</AlertTitle>
                    No se pudo conectar con el servidor. Por favor, verifica tu conexión a internet.
                </Alert>
            )}
        </div>
    );
};

const useStyles = makeStyles()((theme: Theme) => ({
    root: {
        '& > * + *': {
            marginTop: theme.spacing(2),
        },
    },
    errorMessage: {
        marginTop: theme.spacing(0.5),
        '&:first-of-type': {
            marginTop: 0,
        },
    },
}));
